import useCreateLog from '@/log/hooks/useCreateLog'
import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogProps,
  Button,
  ListItem,
  Text,
  UnorderedList,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { RoleSummaryFragment } from '@gql'
import { EntityChangeType, LogType } from '@rolebase/shared/model/log'
import React, { useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { trpc } from 'src/trpc'

interface Props extends Omit<
  AlertDialogProps,
  'children' | 'leastDestructiveRef'
> {
  role: RoleSummaryFragment
}

export default function MakeBaseRoleModal({ role, ...alertProps }: Props) {
  const { t } = useTranslation()
  const { orgId } = useOrgContext()
  const toast = useToast()
  const createLog = useCreateLog()
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Through its own server door (role.makeBase): `base` is outside what
  // role.update writes (GOUV-15). Reserved to org owners, a refusal is shown.
  const [saving, setSaving] = useState(false)
  const handleConfirm = async () => {
    setSaving(true)
    try {
      if (!orgId) throw new Error('Unauthorized')
      await trpc.role.makeBase.mutate({ orgId, roleId: role.id })
      // Logged for the history. Not undoable: `base` is written by the server
      // only (see OrgData.canCancelLog)
      createLog({
        display: { type: LogType.RoleUpdate, id: role.id, name: role.name },
        changes: {
          roles: [
            {
              type: EntityChangeType.Update,
              id: role.id,
              prevData: { base: false },
              newData: { base: true },
            },
          ],
        },
      })
      toast({
        title: t('MakeBaseRoleModal.toast', { name: role.name }),
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
      alertProps.onClose()
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.message || undefined,
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <AlertDialog size="xl" {...alertProps} leastDestructiveRef={cancelRef}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>
            {t('MakeBaseRoleModal.heading')}
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack spacing={4} align="start">
              <Text>
                <Trans
                  i18nKey="MakeBaseRoleModal.info"
                  values={{ name: role.name }}
                  components={{ b: <strong /> }}
                />
              </Text>

              <UnorderedList ml={6}>
                <ListItem>{t('MakeBaseRoleModal.consequence1')}</ListItem>
                <ListItem>{t('MakeBaseRoleModal.consequence2')}</ListItem>
                <ListItem>{t('MakeBaseRoleModal.consequence3')}</ListItem>
              </UnorderedList>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={alertProps.onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              isLoading={saving}
              onClick={handleConfirm}
              ml={3}
            >
              {t('MakeBaseRoleModal.confirmButton')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
